import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useData } from 'contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import Modal from 'components/Layout/Modal';
import ElevatedButton from 'components/Layout/ElevatedButton';
import SubscriptionModal from 'components/Layout/SubscriptionModal';
import DeckEditorModal from 'components/Flashcards/DeckEditorModal';
import { API_ENDPOINTS } from '../api/config';
import { addLocalStudyMinutes, isLocalhost } from '../utils/localStudyTracker';

const Container = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: ${props => props.theme.text};
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
  font-weight: 700;
`;

const Subtitle = styled.p`
  color: ${props => props.theme.textMuted};
  margin: 0;
  font-size: 1.1rem;
  line-height: 1.5;
`;

const DeckGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const DeckCard = styled.div`
  background: ${props => props.theme.backgroundSecondary};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07);
  border: none;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${props => props.theme.background};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const DeckTitle = styled.h3`
  color: ${props => props.theme.text};
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const DeckStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.theme.text};
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CourseTag = styled.span`
  background: ${props => props.color || props.theme.buttonPrimary};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const CreateDeckButton = styled(ElevatedButton)`
  background: ${props => props.theme.buttonPrimary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.buttonPrimaryHover};
  }
`;

const FlashcardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  padding: 2rem 0;
`;

const FlashcardWrapper = styled.div`
  perspective: 1500px;
  width: 100%;
  max-width: 800px;
  height: 450px;
  
  @media (max-width: 768px) {
    height: 300px;
    max-width: 600px;
  }
`;

const FlashcardInner = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  text-align: center;
  transition: transform 0.6s;
  transform-style: preserve-3d;
  cursor: pointer;
  transform: ${props => props.$isFlipped ? 'rotateY(180deg)' : 'rotateY(0)'};
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  will-change: transform;
`;

const FlashcardFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  border-radius: 16px;
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.backgroundSecondary};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 2px solid ${props => props.theme.border || 'rgba(255, 255, 255, 0.1)'};
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
`;

const FlashcardFront = styled(FlashcardFace)``;

const FlashcardBack = styled(FlashcardFace)`
  transform: rotateY(180deg);
`;

const FlashcardLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.theme.textMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 1rem;
`;

const FlashcardContent = styled.div`
  font-size: 1.25rem;
  color: ${props => props.theme.text};
  line-height: 1.6;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const FlashcardControls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 800px;
`;

const ControlButton = styled.button`
  background: ${props => props.theme.backgroundSecondary};
  color: ${props => props.theme.text};
  border: 2px solid ${props => props.theme.border || 'rgba(255, 255, 255, 0.1)'};
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.background};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RatingButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  width: 100%;
  max-width: 800px;
  margin-top: 1rem;
`;

const RatingButton = styled.button`
  flex: 1;
  background: ${props => props.$color || props.theme.backgroundSecondary};
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
    filter: brightness(1.1);
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  max-width: 800px;
  height: 8px;
  background: ${props => props.theme.backgroundSecondary};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 1rem;
  margin-left: auto;
  margin-right: auto;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${props => props.theme.buttonPrimary};
  transition: width 0.3s ease;
  width: ${props => props.$progress}%;
`;

const SessionStatsContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  padding: 1rem;
  background: ${props => props.theme.backgroundSecondary};
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const SessionStat = styled.div`
  text-align: center;
`;

const SessionStatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.$color || props.theme.text};
`;

const SessionStatLabel = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.textMuted};
  text-transform: uppercase;
`;

const ErrorMessage = styled.div`
  color: #f04747;
  padding: 12px 16px;
  border-radius: 6px;
  background: rgba(240, 71, 71, 0.1);
  border: 1px solid rgba(240, 71, 71, 0.2);
  margin-top: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ErrorCloseButton = styled.button`
  background: none;
  border: none;
  color: #f04747;
  cursor: pointer;
  font-size: 18px;
  padding: 0;
  margin-left: 12px;
  opacity: 0.7;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

const Banner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-radius: 14px;
  background: ${props => props.theme.backgroundSecondary};
  border: 1px solid ${props => props.theme.border || 'rgba(255,255,255,0.12)'};
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const BannerText = styled.div`
  display: grid;
  gap: 0.25rem;
`;

const BannerTitle = styled.div`
  color: ${props => props.theme.text};
  font-weight: 700;
`;

const BannerCopy = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: 0.92rem;
`;

const extractFlashcardsFromContent = (content) => {
  try {
    const jsonMatch = content?.match(/\[\s*\{[\s\S]*\}\s*\]/);

    if (jsonMatch) {
      const flashcards = JSON.parse(jsonMatch[0]);
      if (Array.isArray(flashcards)) {
        return flashcards;
      }
    }
  } catch (error) {
    console.error('Error parsing flashcard content:', error);
  }

  return [];
};

const buildStudyCardsFromDeck = (deck, progressData, showCompletedCards) => {
  const cards = [];

  if (deck?.isCustomDeck && Array.isArray(deck.cards)) {
    deck.cards.forEach((card) => {
      const cardProgress = progressData.find((progress) => progress.cardId === card.cardId);

      if (showCompletedCards || !cardProgress?.completed) {
        cards.push({
          id: card.cardId,
          front: card.front,
          back: card.back,
          topic: deck.courseName || deck.title,
          completed: cardProgress?.completed || false
        });
      }
    });

    return cards;
  }

  deck?.insights?.forEach((insight, insightIndex) => {
    const flashcards = extractFlashcardsFromContent(insight.content);

    flashcards.forEach((card, cardIndex) => {
      const front = card.front || card.question || card.term;
      const back = card.back || card.answer || card.definition;

      if (front && back) {
        const cardId = `${insightIndex}-${cardIndex}`;
        const cardProgress = progressData.find((progress) => progress.cardId === cardId);

        if (showCompletedCards || !cardProgress?.completed) {
          cards.push({
            id: cardId,
            front,
            back,
            topic: insight.topic,
            completed: cardProgress?.completed || false
          });
        }
      }
    });
  });

  return cards;
};

const hasPremiumFlashcardAccess = (user) => {
  if (!user) {
    return false;
  }

  if (user.role === 'admin') {
    return true;
  }

  if (user.subscriptionEndDate && user.subscription !== 'lifetime') {
    const endDate = new Date(user.subscriptionEndDate);
    endDate.setHours(12, 0, 0, 0);

    if (new Date() >= endDate) {
      return false;
    }
  }

  const status = user.subscriptionStatus;
  const isCancelled = status === 'canceled' ||
    status === 'cancelled' ||
    status === 'past_due' ||
    status === 'unpaid';

  if (isCancelled || user.cancelAtPeriodEnd) {
    return false;
  }

  return Boolean((user.subscription && user.subscription !== 'free') || (user.aiTier && user.aiTier !== 'free'));
};

const Flashcards = () => {
  const { courses, prefetchData } = useData();
  const { user } = useAuth();
  const [decks, setDecks] = useState([]);
  const [customDecks, setCustomDecks] = useState([]);
  const [isDeckEditorOpen, setIsDeckEditorOpen] = useState(false);
  const [isSavingDeck, setIsSavingDeck] = useState(false);
  const [deckUsage, setDeckUsage] = useState({ decks: 0, cards: 0 });
  const [deckLimits, setDeckLimits] = useState({ deckLimit: 3, cardLimit: 250 });
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [isStudyModalOpen, setIsStudyModalOpen] = useState(false);
  const [focusedCourseId, setFocusedCourseId] = useState(null);
  
  // Study session state
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [studyCards, setStudyCards] = useState([]);
  const [sessionStats, setSessionStats] = useState({ easy: 0, medium: 0, hard: 0 });
  const [showCompletedCards, setShowCompletedCards] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [studyLogLoading, setStudyLogLoading] = useState(false);
  const [isPremiumUser, setIsPremiumUser] = useState(() => hasPremiumFlashcardAccess(user));

  useEffect(() => {
    setIsPremiumUser(hasPremiumFlashcardAccess(user));
  }, [user]);

  const handleStudyDeck = useCallback(async (deck) => {
    setSelectedDeck(deck);

    try {
      // Get progress data for this deck
      const progressResponse = await fetch(`${API_ENDPOINTS.FLASHCARDS}/progress/${deck.scopeId || deck.courseId}?totalCards=${deck.totalCards}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      let progressData = [];
      if (progressResponse.ok) {
        const progressResult = await progressResponse.json();
        if (progressResult.success) {
          progressData = progressResult.progress || [];
        }
      }

      const cards = buildStudyCardsFromDeck(deck, progressData, showCompletedCards);

      setStudyCards(cards);
      setCurrentCardIndex(0);
      setIsFlipped(false);
      setSessionStats({ easy: 0, medium: 0, hard: 0 });
      setIsStudyModalOpen(true);
    } catch (error) {
      console.error('Error loading flashcards for study:', error);
    }
  }, [showCompletedCards]);

  const logFlashcardStudyMinutes = async (minutes) => {
    if (!minutes || Number(minutes) <= 0) return;
    setStudyLogLoading(true);

    if (isLocalhost()) {
      try {
        addLocalStudyMinutes(Number(minutes));
      } catch (error) {
        console.error('Error logging local flashcard study minutes:', error);
      } finally {
        setStudyLogLoading(false);
      }
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.STUDY_TRACKING, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ minutes: Number(minutes) })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          await prefetchData();
        }
      }
    } catch (error) {
      console.error('Error logging flashcard study minutes:', error);
    } finally {
      setStudyLogLoading(false);
    }
  };

  // Check for course query parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const courseId = urlParams.get('course');
    if (courseId) {
      setFocusedCourseId(courseId);
    }
  }, []);

  // Re-filter study cards when showCompletedCards changes
  useEffect(() => {
    if (selectedDeck && isStudyModalOpen) {
      handleStudyDeck(selectedDeck);
    }
  }, [showCompletedCards, handleStudyDeck, isStudyModalOpen, selectedDeck]);

  const refreshCustomDecks = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.FLASHCARD_DECKS, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return;
      }

      const result = await response.json();
      if (!result.success) {
        return;
      }

      setIsPremiumUser(Boolean(result.isPremium));

      const courseLookup = new Map((courses || []).map((course) => [course._id, course]));
      const mappedDecks = await Promise.all((result.decks || []).map(async (deck) => {
        const matchedCourse = deck.courseId ? courseLookup.get(deck.courseId) : null;
        const totalCards = Array.isArray(deck.cards) ? deck.cards.length : deck.cardCount || 0;

        let progressData = { studiedCards: 0, masteryLevel: 0, stillStudyingCards: 0, difficultyBreakdown: {} };

        try {
          const progressResponse = await fetch(`${API_ENDPOINTS.FLASHCARDS}/progress/${deck._id}?totalCards=${totalCards}`, {
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            }
          });

          if (progressResponse.ok) {
            const progressResult = await progressResponse.json();
            if (progressResult.success) {
              progressData = progressResult.stats;
            }
          }
        } catch (progressError) {
          console.error(`Error loading progress for deck ${deck._id}:`, progressError);
        }

        return {
          id: deck._id,
          deckId: deck._id,
          scopeId: deck._id,
          title: deck.title,
          courseId: deck.courseId || null,
          courseName: matchedCourse ? matchedCourse.name : 'Personal deck',
          courseColor: matchedCourse ? matchedCourse.color : '#0ea5e9',
          totalCards,
          studiedCards: progressData.studiedCards,
          stillStudyingCards: progressData.stillStudyingCards || 0,
          masteryLevel: progressData.masteryLevel || 0,
          difficultyBreakdown: progressData.difficultyBreakdown || {},
          sourceType: deck.sourceType,
          sourceName: deck.sourceName,
          cards: deck.cards || [],
          isCustomDeck: true
        };
      }));

      setCustomDecks(mappedDecks);
      setDeckUsage(result.usage || { decks: mappedDecks.length, cards: mappedDecks.reduce((total, deck) => total + deck.totalCards, 0) });
      setDeckLimits(result.limits || { deckLimit: 3, cardLimit: 250 });
    } catch (error) {
      console.error('Error loading custom flashcard decks:', error);
    }
  }, [courses]);

  useEffect(() => {
    refreshCustomDecks();
  }, [refreshCustomDecks]);

  // Load flashcard decks from course learning summaries
  useEffect(() => {
    const loadFlashcardDecks = async () => {
      if (!(courses || []).length) return;

      const decksData = [];
      let focusedCourseDecks = [];

      for (const course of courses) {
        try {
          // Fetch learning summary for this course
          const response = await fetch(API_ENDPOINTS.AI_COURSE_LEARNING_SUMMARY(course._id), {
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
              console.error(`Non-JSON response for course ${course._id}. Content-Type: ${contentType}`);
              continue;
            }

            const summary = await response.json();

            // Count flashcards from insights with context='flashcards'
            let flashcardCount = 0;
            const flashcardInsights = summary.insights?.filter(insight => insight.context === 'flashcards') || [];

            flashcardInsights.forEach(insight => {
              try {
                // Parse JSON flashcard data from content
                const content = insight.content;
                const jsonMatch = content.match(/\[\s*\{[\s\S]*\}\s*\]/); // Match JSON array
                if (jsonMatch) {
                  const flashcards = JSON.parse(jsonMatch[0]);
                  flashcardCount += Array.isArray(flashcards) ? flashcards.length : 0;
                }
              } catch (e) {
                console.error('Error parsing flashcard data:', e);
              }
            });

            if (flashcardCount > 0) {
              // Fetch progress data for this course
              let progressData = { studiedCards: 0, masteryLevel: 0 };
              try {
                const progressResponse = await fetch(`${API_ENDPOINTS.FLASHCARDS}/progress/${course._id}?totalCards=${flashcardCount}`, {
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json'
                  }
                });

                if (progressResponse.ok) {
                  const progressResult = await progressResponse.json();
                  if (progressResult.success) {
                    progressData = progressResult.stats;
                  }
                }
              } catch (progressError) {
                console.error(`Error loading progress for course ${course._id}:`, progressError);
              }

              const deckData = {
                id: course._id,
                title: `${course.course_code} Flashcards`,
                courseId: course._id,
                courseName: course.name,
                courseColor: course.color,
                totalCards: flashcardCount,
                studiedCards: progressData.studiedCards,
                masteryLevel: progressData.masteryLevel,
                insights: flashcardInsights
              };

              if (focusedCourseId === course._id) {
                focusedCourseDecks.push(deckData);
              } else {
                decksData.push(deckData);
              }
            }
          }
        } catch (error) {
          console.error(`Error loading flashcards for course ${course._id}:`, error);
        }
      }

      // Put focused course decks first
      setDecks([...focusedCourseDecks, ...decksData]);
    };

    loadFlashcardDecks();
  }, [courses, focusedCourseId]);

  const visibleDecks = [...customDecks, ...decks];

  const handleCloseModals = () => {
    setIsStudyModalOpen(false);
    setSelectedDeck(null);
    setStudyCards([]);
    setCurrentCardIndex(0);
    setIsFlipped(false);
    setSelectedDifficulty(null);
    setError(null);
  };

  const handleSaveDeck = async (deckPayload) => {
    setIsSavingDeck(true);
    setError(null);

    try {
      const response = await fetch(API_ENDPOINTS.FLASHCARD_DECKS, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(deckPayload)
      });

      const result = await response.json().catch(() => ({}));

      if (response.status === 413) {
        throw new Error(
          result.message || 'This deck is too large to save in one request. Try splitting it into smaller decks.'
        );
      }

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to save flashcard deck.');
      }

      await refreshCustomDecks();
      setIsDeckEditorOpen(false);
    } catch (deckError) {
      console.error('Error saving flashcard deck:', deckError);

      const message = deckError?.message || '';
      if (message === 'Failed to fetch' || message.includes('NetworkError')) {
        setError('Could not save the deck. If you are importing many cards, try splitting the file into smaller decks and import again.');
      } else {
        setError(message || 'Failed to save flashcard deck.');
      }

      throw deckError;
    } finally {
      setIsSavingDeck(false);
    }
  };

  const handleDeleteDeck = async (deckId) => {
    const shouldDelete = window.confirm('Delete this flashcard deck? This will also remove its study progress.');
    if (!shouldDelete) {
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.FLASHCARD_DECK_BY_ID(deckId), {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to delete flashcard deck.');
      }

      await refreshCustomDecks();
    } catch (deckError) {
      console.error('Error deleting flashcard deck:', deckError);
      setError(deckError.message || 'Failed to delete flashcard deck.');
    }
  };
  
  const handleFlipCard = () => {
    setIsFlipped(!isFlipped);
  };
  
  const handleRateCard = async (difficulty, completed = false) => {
    if (!selectedDeck || !studyCards[currentCardIndex]) return;

    try {
      const currentCard = studyCards[currentCardIndex];

      // Save the rating to the backend
      const response = await fetch(API_ENDPOINTS.FLASHCARD_RATE, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          courseId: selectedDeck.scopeId || selectedDeck.courseId,
          cardId: currentCard.id,
          difficulty,
          completed,
          front: currentCard.front,
          back: currentCard.back,
          topic: currentCard.topic,
          totalCards: selectedDeck.totalCards
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          // Update session stats only for difficulty ratings
          setSessionStats(prev => ({ ...prev, [difficulty]: prev[difficulty] + 1 }));

          // If completed, remove from study cards and move to next
          if (completed) {
            setStudyCards(prev => prev.filter((_, index) => index !== currentCardIndex));
            // Adjust currentCardIndex if we removed a card before the current position
            if (currentCardIndex >= studyCards.length - 1) {
              setCurrentCardIndex(Math.max(0, studyCards.length - 2));
            }
            // Always reset flip state for the next card
            setIsFlipped(false);
          } else {
            // Move to next card
            if (currentCardIndex < studyCards.length - 1) {
              setCurrentCardIndex(currentCardIndex + 1);
              setIsFlipped(false);
            } else {
              // Session complete - refresh deck data to show updated stats
                const loadFlashcardDecks = async () => {
              if (!(courses || []).length) return;

              const decksData = [];
              let focusedCourseDecks = [];

              for (const course of courses) {
                try {
                  const response = await fetch(API_ENDPOINTS.AI_COURSE_LEARNING_SUMMARY(course._id), {
                    credentials: 'include',
                    headers: {
                      'Content-Type': 'application/json'
                    }
                  });

                  if (response.ok) {
                    const contentType = response.headers.get('content-type');
                    if (!contentType || !contentType.includes('application/json')) {
                      continue;
                    }

                    const summary = await response.json();

                    let flashcardCount = 0;
                    const flashcardInsights = summary.insights?.filter(insight => insight.context === 'flashcards') || [];

                    flashcardInsights.forEach(insight => {
                      try {
                        const content = insight.content;
                        const jsonMatch = content.match(/\[\s*\{[\s\S]*\}\s*\]/);
                        if (jsonMatch) {
                          const flashcards = JSON.parse(jsonMatch[0]);
                          flashcardCount += Array.isArray(flashcards) ? flashcards.length : 0;
                        }
                      } catch (e) {
                        console.error('Error parsing flashcard data:', e);
                      }
                    });

                    if (flashcardCount > 0) {
                      let progressData = { studiedCards: 0, masteryLevel: 0 };
                      try {
                        const progressResponse = await fetch(`${API_ENDPOINTS.FLASHCARDS}/progress/${course._id}?totalCards=${flashcardCount}`, {
                          credentials: 'include',
                          headers: {
                            'Content-Type': 'application/json'
                          }
                        });

                        if (progressResponse.ok) {
                          const progressResult = await progressResponse.json();
                          if (progressResult.success) {
                            progressData = progressResult.stats;
                          }
                        }
                      } catch (progressError) {
                        console.error(`Error loading progress for course ${course._id}:`, progressError);
                      }

                      const deckData = {
                        id: course._id,
                        title: `${course.course_code} Flashcards`,
                        courseId: course._id,
                        courseName: course.name,
                        courseColor: course.color,
                        totalCards: flashcardCount,
                        studiedCards: progressData.studiedCards,
                        stillStudyingCards: progressData.stillStudyingCards,
                        masteryLevel: progressData.masteryLevel,
                        difficultyBreakdown: progressData.difficultyBreakdown,
                        insights: flashcardInsights
                      };

                      if (focusedCourseId === course._id) {
                        focusedCourseDecks.push(deckData);
                      } else {
                        decksData.push(deckData);
                      }
                    }
                  }
                } catch (error) {
                  console.error(`Error loading flashcards for course ${course._id}:`, error);
                }
              }

              setDecks([...focusedCourseDecks, ...decksData]);
            };

            await refreshCustomDecks();
            await loadFlashcardDecks();
            alert('Study session complete! Great work!');
            handleCloseModals();
            }
          }
        }
      } else {
        console.error('Failed to rate card:', response.status);
        setError('Failed to save your rating. Please try again.');
      }
    } catch (error) {
      console.error('Error rating card:', error);
      setError('An error occurred while saving your rating. Please try again.');
    }
  };
  
  const handlePreviousCard = () => {
    if (currentCardIndex > 0) {
      setCurrentCardIndex(currentCardIndex - 1);
      setIsFlipped(false);
    }
  };
  
  const handleNextCard = () => {
    if (currentCardIndex < studyCards.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
      setIsFlipped(false);
    }
  };

  return (
    <>
      <Container>
        <Banner>
          <BannerText>
            <BannerTitle>Disclaimer:</BannerTitle>
            <BannerCopy>
              As of this moment in time, flashcard decks can be imported as only .txt files, the other file formats are not tested yet, we are working on improving the deck import experience 
              and adding support for more file formats in the near future. If you have any issues or feedback, please reach out to our support team.
            </BannerCopy>
          </BannerText>
        </Banner>
        <Banner>
          <BannerText>
            <BannerTitle>{isPremiumUser ? 'Unlimited flashcards unlocked' : 'Free flashcards are available'}</BannerTitle>
            <BannerCopy>
              {isPremiumUser
                ? 'You can create, import, and study as many decks and flashcards as you want.'
                : `Save up to ${deckLimits.deckLimit} decks and ${deckLimits.cardLimit} flashcards total. Upgrade any time for unlimited decks and unlimited cards.`}
            </BannerCopy>
          </BannerText>
          {!isPremiumUser && (
            <ElevatedButton onClick={() => setIsSubscriptionModalOpen(true)}>
              Upgrade Now
            </ElevatedButton>
          )}
        </Banner>

        <Header>
          <Title>Flashcards</Title>
          <Subtitle>Study your course decks, make your own flashcards, or import decks from Anki, CSV, or TSV exports.</Subtitle>
        </Header>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h2 style={{ margin: 0, color: 'var(--color-text)' }}>Your Flashcard Decks</h2>
            <p style={{ margin: '0.25rem 0 0 0', color: 'var(--color-text-muted)' }}>
              Click on any deck to start studying or delete custom decks you no longer need.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <CreateDeckButton onClick={() => setIsDeckEditorOpen(true)}>
              Create or Import Deck
            </CreateDeckButton>
          </div>
        </div>

        <DeckGrid>
          {visibleDecks.map(deck => (
            <DeckCard key={deck.id} onClick={() => handleStudyDeck(deck)}>
              <DeckTitle>{deck.title}</DeckTitle>
              <CourseTag color={deck.courseColor}>{deck.courseName}</CourseTag>

              {deck.isCustomDeck && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                  <CourseTag color="#64748b">{deck.sourceType || 'manual'}</CourseTag>
                  {deck.sourceName && <CourseTag color="#0ea5e9">{deck.sourceName}</CourseTag>}
                  <CreateDeckButton
                    onClick={(event) => {
                      event.stopPropagation();
                      handleDeleteDeck(deck.id);
                    }}
                    style={{ padding: '0.5rem 0.9rem', marginLeft: 'auto' }}
                  >
                    Delete
                  </CreateDeckButton>
                </div>
              )}

              <DeckStats>
                <StatItem>
                  <StatValue>{deck.totalCards}</StatValue>
                  <StatLabel>Total Cards</StatLabel>
                </StatItem>
                <StatItem>
                  <StatValue>{deck.stillStudyingCards || 0}</StatValue>
                  <StatLabel>Still Studying</StatLabel>
                </StatItem>
                <StatItem>
                  <StatValue>{deck.masteryLevel}%</StatValue>
                  <StatLabel>Mastery</StatLabel>
                </StatItem>
              </DeckStats>

              <div style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                {deck.isCustomDeck
                  ? 'Created manually or imported from another flashcard site'
                  : 'Created from your course materials'}
              </div>
            </DeckCard>
          ))}
        </DeckGrid>

        {error && (
          <ErrorMessage>
            <span>{error}</span>
            <ErrorCloseButton onClick={() => setError(null)}>
              ×
            </ErrorCloseButton>
          </ErrorMessage>
        )}

        {visibleDecks.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)' }}>
            <h3>No flashcard decks yet</h3>
            <p>Create your first deck manually or import one from Anki, CSV, or TSV.</p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap', marginTop: '1rem' }}>
              <CreateDeckButton onClick={() => setIsDeckEditorOpen(true)}>
                Create or Import Deck
              </CreateDeckButton>
            </div>
          </div>
        )}
      </Container>

      {/* Study Modal */}
      <Modal
        isOpen={isStudyModalOpen}
        onClose={handleCloseModals}
        title={selectedDeck ? `Study: ${selectedDeck.title}` : 'Study Session'}
        size="xlarge"
      >
        {selectedDeck && studyCards.length > 0 ? (
          <FlashcardContainer>
            {/* Progress Bar */}
            <div style={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                  Card {currentCardIndex + 1} of {studyCards.length}
                </span>
              </div>
              <ProgressBar>
                <ProgressFill $progress={(currentCardIndex / studyCards.length) * 100} />
              </ProgressBar>
            </div>

            {/* Show Completed Cards Checkbox */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--color-text-muted)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showCompletedCards}
                  onChange={(e) => setShowCompletedCards(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                Show completed cards
              </label>
            </div>

            {/* Session Stats */}
            <SessionStatsContainer>
              <SessionStat>
                <SessionStatValue $color="#4ade80">{sessionStats.easy}</SessionStatValue>
                <SessionStatLabel>Easy</SessionStatLabel>
              </SessionStat>
              <SessionStat>
                <SessionStatValue $color="#fbbf24">{sessionStats.medium}</SessionStatValue>
                <SessionStatLabel>Medium</SessionStatLabel>
              </SessionStat>
              <SessionStat>
                <SessionStatValue $color="#f87171">{sessionStats.hard}</SessionStatValue>
                <SessionStatLabel>Hard</SessionStatLabel>
              </SessionStat>
            </SessionStatsContainer>

            {/* Flashcard */}
            <FlashcardWrapper>
              <FlashcardInner $isFlipped={isFlipped} onClick={handleFlipCard}>
                <FlashcardFront>
                  <FlashcardLabel>Question</FlashcardLabel>
                  <FlashcardContent>{studyCards[currentCardIndex]?.front}</FlashcardContent>
                  <div style={{ marginTop: 'auto', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                    Click to flip
                  </div>
                </FlashcardFront>
                <FlashcardBack>
                  <FlashcardLabel>Answer</FlashcardLabel>
                  <FlashcardContent>{studyCards[currentCardIndex]?.back}</FlashcardContent>
                  <div style={{ marginTop: 'auto', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                    Click to flip back
                  </div>
                </FlashcardBack>
              </FlashcardInner>
            </FlashcardWrapper>

            {/* Navigation Controls */}
            <FlashcardControls>
              <ControlButton 
                onClick={handlePreviousCard} 
                disabled={currentCardIndex === 0}
              >
                ← Previous
              </ControlButton>
              <ControlButton onClick={handleFlipCard}>
                {isFlipped ? 'Flip to Front' : 'Flip to Back'}
              </ControlButton>
              <ControlButton 
                onClick={handleNextCard} 
                disabled={currentCardIndex === studyCards.length - 1}
              >
                Next →
              </ControlButton>
            </FlashcardControls>

            {/* Rating Buttons (only show when flipped) */}
            {isFlipped && (
              <>
                <RatingButtons>
                  {!selectedDifficulty ? (
                    // Step 1: Select difficulty
                    <>
                      <RatingButton
                        $color="#f87171"
                        onClick={() => setSelectedDifficulty('hard')}
                      >
                        Hard
                      </RatingButton>
                      <RatingButton
                        $color="#fbbf24"
                        onClick={() => setSelectedDifficulty('medium')}
                      >
                        Medium
                      </RatingButton>
                      <RatingButton
                        $color="#4ade80"
                        onClick={() => setSelectedDifficulty('easy')}
                      >
                        Easy
                      </RatingButton>
                    </>
                  ) : (
                    // Step 2: Choose completion status
                    <>
                      <RatingButton
                        $color="#6b7280"
                        onClick={() => {
                          handleRateCard(selectedDifficulty, false);
                          setSelectedDifficulty(null);
                        }}
                      >
                        Still Studying
                      </RatingButton>
                      <RatingButton
                        $color="#059669"
                        onClick={() => {
                          handleRateCard(selectedDifficulty, true);
                          setSelectedDifficulty(null);
                        }}
                      >
                        Done Studying
                      </RatingButton>
                    </>
                  )}
                </RatingButtons>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1rem' }}>
                  <ElevatedButton
                    onClick={() => logFlashcardStudyMinutes(30)}
                    disabled={studyLogLoading}
                  >
                    Log 30 min study
                  </ElevatedButton>
                  <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem', alignSelf: 'center' }}>
                    Count this flashcard session toward your streak and points.
                  </span>
                </div>
              </>
            )}
          </FlashcardContainer>
        ) : selectedDeck ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>
            <h3>No flashcards found</h3>
            <p>This deck doesn't have any flashcards yet. Add cards manually or import a deck to start studying.</p>
          </div>
        ) : null}
      </Modal>

      <DeckEditorModal
        isOpen={isDeckEditorOpen}
        onClose={() => setIsDeckEditorOpen(false)}
        onSave={handleSaveDeck}
        courses={courses || []}
        initialCourseId={focusedCourseId || ''}
        isPremium={isPremiumUser}
        usage={deckUsage}
        limits={deckLimits}
        saving={isSavingDeck}
      />

      <SubscriptionModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
      />
    </>
  );
};

export default Flashcards;
